import React from 'react';

import { Interval, DateTime, Duration } from 'luxon';

import {gifshot} from './utils/gifshot.js';

import MapWidget from './widgets/MapWidget';
import ControlWidget from './widgets/ControlWidget';
import TornadoCollection from './utils/TorCollection';
import {f_scale_partitioner, Partitioner} from './utils/Partitioner';
import {EnvironmentDataInterface, EnvironmentSource, LayerType} from './utils/EnvironmentDataHandler';
import {SelectionInterval} from './utils/SelectionInterval';
import MapOverlayWidget from './widgets/MapOverlayWidget';
import { Filter, PartitionFilter } from './utils/Filter';
import Animator from './utils/Animator';
import { hex2rgba } from './utils/utils';
import MultiscaleTimeAxis from './utils/MultiscaleTimeAxis';
import { Tornado, TornadoProperties } from './utils/tornado';
import { DOMAINS, Domain, ENV_INTV_DUR_CUTOFF, USE_DEV_SERVER, TIMELINE_MARGINS, DATA_LIMITS, ENV_DATA_BASE, DOMAIN_TITLES, 
         TemporalDisplayOpt, GIFQualityOpt, MapState, TIMELINE_SEGMENTS, FileDownloadFormat } from './widgets/Constants';
import { downloadCSV, downloadGeoJSON, downloadKML } from './utils/dataDownload';
import { randomChoice } from './utils/utils';

import './App.css';

interface URLFragments {
    interval?: SelectionInterval;
    map?: MapState | null;
    env_src?: EnvironmentSource | null;
    env_type?: LayerType | null;
    sel_data_time?: DateTime | null;
    domain?: Domain;
    filters?: Record<string, Filter>;
}

interface TornadoArchiveAppProps {
    default_domain: Domain;
    default_day: DateTime;
}

interface TornadoArchiveAppState {
    controls_visible: boolean;

    interval: SelectionInterval;

    filters: Record<string, Filter>;

    timeline_taxis: MultiscaleTimeAxis;

    sel_data_time: DateTime | null;
    env_data_times: DateTime[];
    env_type: LayerType | null;
    env_src: EnvironmentSource | null;

    map: MapState | null;

    tor_collection: TornadoCollection | null;

    is_animating: boolean;
    temp_display: TemporalDisplayOpt;

    domain: Domain,

    window_size: {width: number, height: number};

    tornado_info: Tornado | null;

    is_loading_tors: boolean;
    is_drawing_tors: boolean;
}

class TornadoArchiveApp extends React.Component<TornadoArchiveAppProps, TornadoArchiveAppState> {
    base_tor_collection: TornadoCollection | null;
    domain_tor_collection: TornadoCollection | null;
    partitioner: Partitioner;

    default_cday: DateTime;

    env_interfaces: Record<string, Record<EnvironmentSource, EnvironmentDataInterface>>;
    file_download_check_env_data: boolean;

    animator: Animator;
    anim_delay: number;

    initial_window_width: number;
    initial_window_height: number;

    ss_funcs: Record<string, () => Promise<HTMLCanvasElement>>;

    stale_url_fragment: boolean;

    is_mounted: boolean;

    constructor(props: TornadoArchiveAppProps) {
        super(props);

        this.base_tor_collection = null;
        this.domain_tor_collection = null;
        this.partitioner = f_scale_partitioner;

        const default_domain = this.props['default_domain'];
        const cday_offset = DOMAINS[default_domain]['cday_offset']

        this.default_cday = this.props['default_day'];
        const intv = new SelectionInterval(Interval.after(this.default_cday.plus(cday_offset), {'hours': 24}));

        this.env_interfaces = {};

        this.anim_delay = 500; // milliseconds
        this.animator = new Animator(this.anim_delay, this.advanceTime.bind(this), (dt: DateTime) => {

            this.setState({'sel_data_time': dt});
            this._updateURLFragment({'sel_data_time': dt});
            return false;
        });

        const window_obj = window.self !== window.top ? window.parent : window;
        const url_fragment = window_obj.location.hash.slice(1);
        const frag_state = this._parseURLFragment(url_fragment);
        this.file_download_check_env_data = frag_state['sel_data_time'] === undefined;

        this.initial_window_width = window.innerWidth;
        this.initial_window_height = window.innerHeight;
        if (window.innerWidth > window.innerHeight) {
            this.initial_window_width = window.innerHeight;
            this.initial_window_height = window.innerWidth;
        }

        const default_state = {
            'controls_visible': true,

            'interval': intv,

            'filters': {
                'partition': new PartitionFilter(this.partitioner)
            },

            'sel_data_time': null,
            'env_data_times': [],
            'env_type': null,
            'env_src': null,

            'map': null,

            'tor_collection': null,

            'is_animating': false,
            'temp_display': 'All' as TemporalDisplayOpt,

            'domain': default_domain,

            'window_size': {'width': window.innerWidth, 'height': window.innerHeight},

            'tornado_info': null,

            // If you need to add a third, make it loading flags dictionary
            'is_loading_tors': false,
            'is_drawing_tors': false,
        };

        const state = {...default_state, ...frag_state};

        const dom_data_limits = Interval.fromDateTimes(DATA_LIMITS.start.plus(cday_offset), DATA_LIMITS.end.plus(cday_offset));
        const taxis = new MultiscaleTimeAxis(state['interval'].getPaddedInterval(), 
                                           [TIMELINE_MARGINS['left'], state['window_size']['width'] - TIMELINE_MARGINS['right']], dom_data_limits, 15,
                                           TIMELINE_SEGMENTS);

        this.state = {
            ...state, 
            'timeline_taxis': taxis.withInterval(state['interval'].getPaddedInterval())
        };

        // This will break things when the interval start is not during a day with some initial data
        this.default_cday = this.state['interval'].start();

        this.ss_funcs = {};
        this.set_screenshot_func = this.set_screenshot_func.bind(this);
        this.stale_url_fragment = false;

        this.keypress = this.keypress.bind(this);
        this.resize = this.resize.bind(this);

        this.onshowhidecontrols = this.onshowhidecontrols.bind(this);
        this.onintervalchange = this.onintervalchange.bind(this);
        this.onfilterchange = this.onfilterchange.bind(this);
        this.ontboundschange = this.ontboundschange.bind(this);
        this.onenvlayerchange = this.onenvlayerchange.bind(this);
        this.onenvtimechange = this.onenvtimechange.bind(this);
        this.onanimtoggle = this.onanimtoggle.bind(this);
        this.onanimspeedchange = this.onanimspeedchange.bind(this);
        this.onscreenshot = this.onscreenshot.bind(this);
        this.oncreategif = this.oncreategif.bind(this);
        this.ondomainchange = this.ondomainchange.bind(this);
        this.ontempdisplaychange = this.ontempdisplaychange.bind(this);
        this.onloadingstart = this.onloadingstart.bind(this);
        this.onloadingend = this.onloadingend.bind(this);
        this.onopentornadoinfo = this.onopentornadoinfo.bind(this);
        this.ondownloadfile = this.ondownloadfile.bind(this);

        this.is_mounted = false;
    }

    async componentDidMount() {
        window.addEventListener('keydown', this.keypress);
        window.addEventListener('resize', this.resize);

        if (this.is_mounted) return;

        this.is_mounted = true;

        this._updateURLFragment();

        if (this.state['env_src'] !== null && this.state['env_type'] !== null) {
            this.checkEnvDataTimes(this.state['domain'], this.state['env_src'], this.state['env_type'], this.state['interval'], this.state['tor_collection'], true);
        }

        const url_base = USE_DEV_SERVER ? '/dev/data' : '/data';

        const now = DateTime.utc();
        this.setState({'is_loading_tors': true});

        const downloadFile = async (url: string) => {
            let tor_coll = await TornadoCollection.fromServer(url + '?' + now.toISO());

            if (this.base_tor_collection !== null) {
                this.base_tor_collection.deleteInstance();
            }
            this.base_tor_collection = tor_coll;

            const new_domain_tor_collection = await tor_coll.applyFilters([DOMAINS[this.state['domain']]['filter']]);
            if (this.domain_tor_collection !== null) {
                this.domain_tor_collection.deleteInstance();
            }
            this.domain_tor_collection = new_domain_tor_collection;

            const new_tor_collection = await this.domain_tor_collection.applyFilters(Object.values(this.state['filters']));
            if (this.state['tor_collection'] !== null) {
                this.state['tor_collection'].deleteInstance();
            }
            this.setState({'tor_collection': new_tor_collection});

            if (this.file_download_check_env_data) {
                this.changeInterval(this.state['interval'], new_tor_collection, false);
                this.file_download_check_env_data = false;
            }
        }

        const sd_intvs = DOMAINS[this.state['domain']]['small_datafile_intvs'].filter(intv => {
            const [ylb, yub] = intv;
            if (ylb !== null && yub !== null) {
                return ylb <= this.default_cday.year && this.default_cday.year <= yub;
            }
            else if (ylb === null && yub !== null) {
                return this.default_cday.year <= yub;
            }
            else if (ylb !== null && yub === null) {
                return this.default_cday.year >= ylb;
            }
            else {
                return true;
            }
        });

        let yr_str: string;
        if (sd_intvs.length > 0) {
            const [sd_ylb, sd_yub] = sd_intvs[0];
            if (sd_ylb === null && sd_yub === null) {
                yr_str = '';
            }
            else if (sd_ylb === null) {
                yr_str = '_None-' + sd_yub;
            }
            else if (sd_ylb == sd_yub) {
                yr_str = '_' + sd_ylb;
            }
            else {
                yr_str = '_' + sd_ylb + '-' + sd_yub;
            }
        }
        else {
            yr_str = '';
        }

        try {
            await downloadFile(url_base + '/' + DOMAINS[this.state['domain']]['env_name'] 
                               + '/tornadoes' + yr_str + '.json.gz');
        }
        catch (e) {
            console.warn("Couldn't download yearly subset");
        }

        await downloadFile(url_base + '/tornadoes.json.gz');
        this.setState({'is_loading_tors': false});
        
        //const tor = await this.domain_tor_collection.getGeoJSONById('SPC49749_NCEI35937');
        //this.setState({'tornado_info': tor});
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.keypress);
        window.removeEventListener('resize', this.resize);
    }

    onintervalchange(intv: SelectionInterval, recenter_timeline?: boolean, clear_infobox?: boolean) {
        const new_state = this.changeInterval(intv, this.state['tor_collection'], recenter_timeline, clear_infobox);
        this.setState(new_state as TornadoArchiveAppState);
    }

    changeInterval(intv: SelectionInterval, tor_coll: TornadoCollection | null, recenter_timeline?: boolean, clear_infobox?: boolean) {
        let reset_sel_data_time: boolean;
        let new_state: Record<string, any>;
        if (recenter_timeline) {
            const intv_pad = intv.getPaddedInterval();
            new_state = {'interval': intv, 'timeline_taxis': this.state['timeline_taxis'].withInterval(intv_pad), 'sel_data_time': null};
            reset_sel_data_time = true;
        }
        else {
            new_state = {'interval': intv};
            reset_sel_data_time = (this.state['sel_data_time'] === null);
        }

        if (intv.duration() >= ENV_INTV_DUR_CUTOFF && this.state['temp_display'] == 'Hourly') {
            new_state['temp_display'] = 'All';
        }

        if (clear_infobox) {
            new_state['tornado_info'] = null;
        }

        let new_src = this.state['env_src'];
        const env_type = this.state['env_type'];

        if (new_src !== null && env_type !== null) {
            const intfcs = this.getEnvInterface(this.state['domain']);
            if (intfcs !== null) {
                const cday_offset = DOMAINS[this.state['domain']]['cday_offset'];
                const intv_raw = intv.getIntervals()[0];
                const sources = Object.entries(intfcs).filter(([source, intfc]) => intfc.availableDaysInIntervals([intv_raw], cday_offset)[0] > 0)
                                                      .map(([source, intfc]) => source) as EnvironmentSource[];

                if (sources.length > 0 && !sources.includes(new_src)) {
                    new_src = sources[0];
                }
            }
            this.checkEnvDataTimes(this.state['domain'], new_src, env_type, intv, tor_coll, reset_sel_data_time);
            new_state['env_src'] = new_src;
        }

        this._updateURLFragment({'interval': intv, 'env_src': new_src});
        return new_state;
    }

    ontboundschange(tbnds: Interval) {
        const tbnds_intv = new SelectionInterval(tbnds);
        this.setState({'timeline_taxis': this.state['timeline_taxis'].withInterval(tbnds_intv)});
    }

    async onfilterchange(new_filters: Record<string, Filter | null>) {
        const new_state = await this.changeFilters(new_filters, this.state['filters'], this.state['tor_collection']);
        this.setState(new_state as TornadoArchiveAppState);
    }

    async changeFilters(new_filters: Record<string, Filter | null>, cur_filters: Record<string, Filter | null>, cur_tor_collection: TornadoCollection | null) {
        new_filters = {...cur_filters, ...new_filters};
        const new_filters_keep = Object.fromEntries(Object.entries(new_filters).filter(([k, v]) => v !== null)) as Record<string, Filter>;

        let new_state: Record<string, any> = {'filters': new_filters_keep};

        if (this.domain_tor_collection !== null) {
            new_state['tor_collection'] = await this.domain_tor_collection.applyFilters(Object.values(new_filters_keep));
        }

        if (cur_tor_collection !== null && cur_tor_collection.key !== new_state['tor_collection'].key) {
            cur_tor_collection.deleteInstance();
        }

        this._updateURLFragment({'filters': new_filters_keep});

        return new_state;
    }

    onscreenshot() {
        let win = window.open();
        if (win !== null) {
            const header = `<head>
            <title>Tornado Archive Screenshot [Rendering ...]</title>
            <style>
            h1 { width: 100%; text-align: center; position: fixed; top: 50%; transform: translate(0, -50%); }
            </style>
            </head>`;
            const body = "<body><h1>Rendering Screenshot ...</h1></body>";

            win.document.open();
            win.document.write(header + body);
            win.document.location = '#';
            win.document.close();

            this.screenshotCurrentFrame().then((canvas) : Promise<HTMLCanvasElement> => {
                return new Promise((resolve, reject) => {
                    window.setTimeout(() => resolve(canvas), 30);
                });
            }).then(canvas => {
                if (win === null) return;

                const header = "<head><title>Tornado Archive Screenshot</title></head>";
                const iframe = "<body><img width='100%' src='" + String(canvas.toDataURL()) + "'></body>";

                win.document.open();
                win.document.write(header + iframe);
                win.document.location = '#';
                win.document.close();
            });
        }
    }

    oncreategif(quality: GIFQualityOpt) {
        if (this.state['sel_data_time'] === null) return;

        let frames: Record<string, Promise<HTMLCanvasElement>> = {};
        let last_dt = this.state['sel_data_time'];

        const gif_quality_settings = {
            'low': {'ncolors': 32, 'pixel_fac': 2},
            'medium': {'ncolors': 128, 'pixel_fac': 2},
            'high': {'ncolors': 256, 'pixel_fac': 1},
        }[quality];

        const assemble_gif = () => {
            let win = window.open();

            const writePage = (progress: number) => {
                if (win === null) return;

                const header = `<head>
                <title>Tornado Archive GIF [Rendering ...]</title>
                <style>
                div#container { width: 100%; text-align: center; position: fixed; top: 50%; transform: translate(0, -50%); }
                div#progress { width: 50%; height: 15px; margin-left: auto; margin-right: auto; }
                div#progress div { height: 100%; display: inline-block; }
                div#progress #completed { width: ${progress * 100}%; background-color: #000000; }
                div#progress #incomplete { width: ${(1 - progress) * 100}%; background-color: #dddddd; }
                </style>
                </head>`;
                const body = `<body>
                    <div id="container">
                        <h1>Rendering GIF ...</h1>
                        <div id="progress">
                            <div id="completed"></div><div id="incomplete"></div>
                        </div>
                    </div>
                </body>`;

                win.document.open();
                win.document.write(header + body);
                win.document.location = '#';
                win.document.close();
            }

            writePage(0);

            const frame_promises = Object.keys(frames).sort().map(fdt => frames[fdt]);
            
            Promise.all(frame_promises).then(frames => {
                const gif_width = frames[0].width / gif_quality_settings['pixel_fac'];
                const gif_height = frames[0].height / gif_quality_settings['pixel_fac'];

                return new Promise((resolve, reject) => {
                    const gif_options = {'images': frames, 'gifWidth': gif_width, 'gifHeight': gif_height, 'ncolors': gif_quality_settings['ncolors'], 'disposal': 1,
                                         'sampleInterval': gif_quality_settings['pixel_fac'], 'interval': this.anim_delay / 1000, 'progressCallback': writePage,
                                         'colorHints': this.partitioner.colors.map(color => hex2rgba(color, 'int'))};
                    gifshot.createGIF(gif_options, (obj: {image: string, error: string}) => {
                        if (!obj.error) {
                            resolve(obj.image);
                        }
                        else {
                            reject(obj.error);
                        }
                    })
                });
            }).then(img => {
                if (win === null) return;

                const header = "<head><title>Tornado Archive GIF</title></head>";
                const iframe = "<body><img width='100%' src='" + img + "'></body>";

                win.document.open();
                win.document.write(header + iframe);
                win.document.location = '#';
                win.document.close();
            });
        }

        const animator: Animator = new Animator(100, () => {
            if (this.state['sel_data_time'] === null) return false;

            return animator.advanceTime(1, this.state['sel_data_time'], this.state['env_data_times']);
        }, dt => {
            let retval = false;
            if (frames.hasOwnProperty(last_dt.toISO())) {
                retval = true;
                assemble_gif();

                this.animator.toggleAnimation(is_animating);
                this.setState({'is_animating': is_animating});

                window.addEventListener('keydown', this.keypress);
            }
            else {
                frames[last_dt.toISO()] = this.screenshotCurrentFrame();
                last_dt = dt;
                this.setState({'sel_data_time': dt});
            }

            return retval;
        });

        const is_animating = this.animator.isAnimating();
        this.animator.toggleAnimation(false);

        window.removeEventListener('keydown', this.keypress);
        animator.toggleAnimation(true);
        this.setState({'is_animating': true});
    }

    screenshotCurrentFrame() {
        // Poll all the child components for screenshots
        const promise_map = this.ss_funcs['map']();
        const promise_overlay = this.ss_funcs['overlay']();
        const promise_timeline = this.ss_funcs['timeline']();

        // Composite them and open a new window
        return Promise.all([promise_map, promise_overlay, promise_timeline]).then(results => {
            const [map_canvas, overlay_canvas, timeline_canvas] = results;

            const full_width = map_canvas.width;
            const full_height = map_canvas.height + timeline_canvas.height + 4;

            let canvas = document.createElement('canvas');
            canvas.width = full_width;
            canvas.height = full_height;

            let ctx = canvas.getContext('2d');
            if (ctx === null) {
                throw "Could not get a drawing context in the app screenshot function";
            }

            ctx.beginPath();
            ctx.fillStyle = '#ffffff';
            ctx.rect(0, 0, full_width, full_height);
            ctx.fill();

            ctx.drawImage(map_canvas, 0, 0);
            ctx.drawImage(overlay_canvas, 0, 0);
            ctx.drawImage(timeline_canvas, 0, map_canvas.height + 4);

            return canvas;
        });
    }

    set_screenshot_func(ss_func: () => Promise<HTMLCanvasElement>, which: string) {
        this.ss_funcs[which] = ss_func;
    }

    _updateURLFragment(data?: URLFragments) {
        if (data === undefined) {
            data = {};
        }

        let keys: Record<keyof URLFragments, Function> = {
            'interval': (i: SelectionInterval) => i.toURLFragment(),
            'map': (m: MapState) => m['center_lon'].toFixed(4) + ';' + m['center_lat'].toFixed(4) + ';' + m['zoom'].toFixed(2),
            'env_src': (s: EnvironmentSource) => s,
            'env_type': (t: LayerType) => t,
            'sel_data_time': (t: DateTime) => t.toISO(),
            'domain': (d: Domain) => d,
            'filters': (f: Record<string, Filter>) => Object.entries(f).map(([fn, f_]) => fn + '|' + f_.toRepr()).join(';'),
        };

        let url_frags = Object.fromEntries(Object.keys(keys).map(k => [k, this.state[k as keyof URLFragments]]));
        url_frags = {...url_frags, ...data};

        if (this.state['sel_data_time'] === null || url_frags['sel_data_time'] === null) {
            delete url_frags['sel_data_time'];
        }

        if (this.state['map'] === null || url_frags['map'] === null) {
            delete url_frags['map'];
        }
        
        const frag_str = Object.entries(url_frags).map(([k, v]) =>  k + '=' + keys[k as keyof URLFragments](v)).join('&');

        const window_obj = window.self !== window.top ? window.parent : window;
        const url_no_hash = window_obj.location.href.split('#')[0];
        window_obj.history.replaceState(null, '', url_no_hash + '#' + frag_str);
    }

    _parseURLFragment(url_fragment: string) : URLFragments {
        const map_keys = ['center_lon', 'center_lat', 'zoom'];

        const keys: Record<keyof URLFragments, Function> = {
            'interval': (i: string, o: Duration) => {
                const dom_data_limits = Interval.fromDateTimes(DATA_LIMITS.start.plus(o), DATA_LIMITS.end.plus(o));
                return SelectionInterval.fromURLFragment(i, o, dom_data_limits);
            },
            'map': (m: string) => Object.fromEntries(m.split(';').map((mv, imv) => [map_keys[imv], parseFloat(mv)])),
            'env_src': (s: string) => s == 'null' ? null : s as EnvironmentSource,
            'env_type': (t: string) => t == 'null' ? null : t as LayerType,
            'sel_data_time': (t: string) => DateTime.fromISO(t, {'zone': 'utc'}),
            'domain': (d: string) => decodeURI(d) as Domain,
            'filters': (f: string) => Object.fromEntries(decodeURI(f).split(';').map(f_ => {
                const filter_bits = f_.split('|');
                return [filter_bits[0], Filter.fromRepr(filter_bits.slice(1).join('|'))];
            })),
        };

        const parse_entry = (entry: string, other?: any) => {
            const bits = entry.split('=');
            const item_name = bits[0] as keyof URLFragments;
            const item_str = bits.slice(1).join("=");

            if (item_name in keys) {
                const item = keys[item_name](item_str, other);
                return [item_name, item];
            }
            return null;
        }

        const entry_strs = url_fragment.split('&');
        let url_frags: URLFragments = Object.fromEntries(entry_strs.filter(entry => entry != '')
                                                                   .filter(entry => !entry.startsWith('interval'))
                                                                   .map(parse_entry)
                                                                   .filter((entry): entry is any[] => entry !== null));

        const domain = url_frags['domain'] === undefined ? this.props['default_domain'] : url_frags['domain'];

        const intv_entry = entry_strs.filter(entry => entry.startsWith('interval'))
                                     .map(entry => parse_entry(entry, DOMAINS[domain]['cday_offset']))
                                     .filter((entry): entry is any[] => entry !== null)[0];

        if (intv_entry !== undefined) {
            url_frags['interval'] = intv_entry[1];
        }

        return url_frags;
    }
    
    onenvtimechange(new_time: DateTime | null) {
        const new_state = this.changeEnvTime(new_time);
        this.setState(new_state as TornadoArchiveAppState);
    }

    changeEnvTime(new_time: DateTime | null) {
        this._updateURLFragment({'sel_data_time': new_time});
        return {'sel_data_time': new_time} as Record<string, any>;
    }

    async onenvlayerchange(new_src: EnvironmentSource | null, new_type: LayerType | null) {
        const new_state = await this.changeEnvLayer(new_src, new_type);
        this.setState(new_state as TornadoArchiveAppState);
    }

    async changeEnvLayer(new_src: EnvironmentSource | null, new_type: LayerType | null) {
        const has_new_src = new_src !== null && new_type !== null && new_src != this.state['env_src'];

        this._updateURLFragment({'env_src': new_src, 'env_type': new_type});

        if (has_new_src) {
            this.checkEnvDataTimes(this.state['domain'], new_src, new_type, this.state['interval'], this.state['tor_collection'], true);
        }

        return has_new_src ? {} : {'env_src': new_src, 'env_type': new_type} as Record<string, any>;
    }

    async onnewenvdatatimes(domain: Domain, source: EnvironmentSource, type: LayerType, new_env_data_times: DateTime[], interval: SelectionInterval,
        tor_coll: TornadoCollection | null, reset_sel_data_time: boolean) {

        let new_state: Record<string, any> = {'env_data_times': new_env_data_times, 'env_src': source, 'env_type': type};
        const interest = (cnt: Record<string, number>) => {
            return Object.entries(cnt).map(([k, v]) => {
                const part_idx = this.partitioner.part_keys.indexOf(k);
                return this.partitioner.part_interest[part_idx] * v;
            }).reduce((a, b) => a + b, 0);
        }

        let prio_env_time: DateTime | null = null;
        const intfc = this.getEnvInterface(domain, source);
        if (reset_sel_data_time && new_env_data_times.length > 0 && tor_coll !== null && intfc !== null) {
            const dt_reanalysis = intfc.getAnalysisTimeStep();

            const tc_intvs = interval.getSubintervals(dt_reanalysis, DOMAINS[domain]['cday_offset']);
            const tc_intvs_clipped = tc_intvs.map(sintv => interval.intersection(sintv)).filter((sintv): sintv is Interval => sintv !== null);

            const tc_counts = await tor_coll.getNTornadoes(tc_intvs_clipped, this.partitioner);
            const tc_interest = tc_counts.map(cnt => interest(cnt))
            const count_max_idx = tc_interest.indexOf(Math.max(...tc_interest));
            prio_env_time = tc_intvs[count_max_idx].start;

            if (new_env_data_times.map(dt => dt.toISO()).includes(prio_env_time.toISO())) {
                new_state['sel_data_time'] = prio_env_time;
            }
            else {
                new_state['sel_data_time'] = null;
            }
        }
        else if (new_env_data_times.length == 0) {
            new_state['sel_data_time'] = null;
        }

        this.setState(new_state as TornadoArchiveAppState);

        if (new_state['sel_data_time'] !== undefined) {
            this._updateURLFragment({'interval': interval, 'sel_data_time': new_state['sel_data_time']});
        }
    }

    onanimtoggle() {
        const turn_on_anim = !this.state['is_animating']
        this.setState({'is_animating': turn_on_anim});
        this.animator.toggleAnimation(turn_on_anim);
    }

    onanimspeedchange(amount: number) {
        this.animator.anim_delay *= Math.pow(1.5, -amount);
    }

    advanceTime(direction?: number) {
        if (this.state['sel_data_time'] === null) {
            return false;
        }

        if (direction === undefined) {
            direction = 1;
        }

        this.animator.advanceTime(direction, this.state['sel_data_time'], this.state['env_data_times']);
        return false;
    }

    async ondomainchange(new_domain: Domain) {
        const new_state = await this.changeDomain(new_domain, this.state['filters'], this.state['tor_collection']);
        this.setState(new_state as TornadoArchiveAppState);
    }

    async changeDomain(new_domain: Domain, cur_filters: Record<string, Filter | null>, cur_tor_collection: TornadoCollection | null) {
        const new_state: Record<string, any> = {'domain': new_domain};
        if (this.base_tor_collection !== null) {
            const new_domain_tor_collection = await this.base_tor_collection.applyFilters([DOMAINS[new_domain]['filter']]);

            if (this.domain_tor_collection !== null && this.domain_tor_collection.key != new_domain_tor_collection.key) {
                this.domain_tor_collection.deleteInstance();
            }
            this.domain_tor_collection = new_domain_tor_collection;
        }

        if (this.state['env_src'] !== null && this.state['env_type'] !== null) {
            this.checkEnvDataTimes(new_domain, this.state['env_src'], this.state['env_type'], this.state['interval'], this.state['tor_collection'], true);
        }

        const filter_mod_state = await this.changeFilters({}, cur_filters, cur_tor_collection);
        return {...new_state, ...filter_mod_state};
    }

    onmapmove(new_map_info: MapState) {
        this.setState({'map': new_map_info});
        this.stale_url_fragment = true;
    }

    onmapready() {
        if (this.stale_url_fragment) {
            this._updateURLFragment({'map': this.state['map']});
            this.stale_url_fragment = false;
        }
    }

    async checkEnvDataTimes(domain: Domain, source: EnvironmentSource, type: LayerType, interval: SelectionInterval, tor_coll: TornadoCollection | null, 
        reset_sel_data_time: boolean) {
        if (interval.duration() > ENV_INTV_DUR_CUTOFF) {
            this.onnewenvdatatimes(domain, source, type, [], interval, tor_coll, reset_sel_data_time);
            return;
        }

        const cday_offset = DOMAINS[domain]['cday_offset'];

        if (!this.hasEnvInterface(domain, source)) {
            const get_all_reanalyses = async () => {
                const dom_env_name = DOMAINS[domain]['env_name'];
                return Promise.all(EnvironmentDataInterface.getAvailableReanalyses().map(async (source) => {
                    const intfc = await EnvironmentDataInterface.fromServer(ENV_DATA_BASE, dom_env_name, source);
                    return [source, intfc] as [EnvironmentSource, EnvironmentDataInterface];
                }));
            }
            const intfcs = Object.fromEntries(await get_all_reanalyses()) as Record<EnvironmentSource, EnvironmentDataInterface>;
            this.setEnvInterface(domain, intfcs);
        }

        const intfc = this.getEnvInterface(domain, source);
        if (intfc === null) {
            throw "Interface is still null for some reason.";
        }

        const dt_reanalysis = intfc.getAnalysisTimeStep();

        const dts = interval.getDatetimes(dt_reanalysis, cday_offset);
        this.onnewenvdatatimes(domain, source, type, dts.filter(dt => intfc.hasData(dt, cday_offset)), interval, tor_coll, reset_sel_data_time);
    }

    onshowhidecontrols() {
        this.setState({'controls_visible': !this.state['controls_visible']});
    }

    ontempdisplaychange(display: TemporalDisplayOpt) {
        this.setState({'temp_display': display});
    }

    onloadingstart(what: string) {
        const state_key = 'is_' + what;

        let new_state: Record<string, any> = {};
        new_state[state_key] = true;

        this.setState(new_state as TornadoArchiveAppState);
    }

    onloadingend(what: string) {
        const state_key = 'is_' + what;

        let new_state: Record<string, any> = {};
        new_state[state_key] = false;

        this.setState(new_state as TornadoArchiveAppState);
    }

    onopentornadoinfo(tornado: Tornado) {
        this.setState({'tornado_info': tornado});
    }

    onclosetornadoinfo() {
        this.setState({'tornado_info': null});
    }

    keypress(event: KeyboardEvent) {
        switch (event.key) {
            case '.':
                this.advanceTime(1);
                break;
            case ',':
                this.advanceTime(-1);
                break;
            case ' ':
                this.onanimtoggle();
                break;
        }
    }
    
    resize() {
        const new_width = window.innerWidth > window.innerHeight ? this.initial_window_height : this.initial_window_width;
        const new_height = window.innerWidth > window.innerHeight ? this.initial_window_width : this.initial_window_height;
        const timeline_xbnds: [number, number] = [TIMELINE_MARGINS['left'], new_width - TIMELINE_MARGINS['right']];

        this.setState({'window_size': {'width': new_width, 'height': new_height}, 
            'timeline_taxis': this.state['timeline_taxis'].withXBounds(timeline_xbnds)});
    }

    getEnvInterface(domain: Domain) : Record<EnvironmentSource, EnvironmentDataInterface> | null;
    getEnvInterface(domain: Domain, source: EnvironmentSource) : EnvironmentDataInterface | null;
    getEnvInterface(domain: Domain, source?: EnvironmentSource) : EnvironmentDataInterface | Record<EnvironmentSource, EnvironmentDataInterface> | null {
        const dom_env_name = DOMAINS[domain]['env_name'];
        if (!(dom_env_name in this.env_interfaces)) {
            return null;
        }

        if (source === undefined) {
            return this.env_interfaces[dom_env_name];
        }

        return this.env_interfaces[dom_env_name][source];
    }

    hasEnvInterface(domain: Domain, source: EnvironmentSource) {
        return this.getEnvInterface(domain, source) !== null;
    }

    setEnvInterface(domain: Domain, intfcs: Record<EnvironmentSource, EnvironmentDataInterface>) {
        const dom_env_name = DOMAINS[domain]['env_name'];
        this.env_interfaces[dom_env_name] = intfcs;
    }

    async ondownloadfile(format: FileDownloadFormat) {
        const has_eswd = (src: TornadoProperties['source']) => {
            return (src !== undefined && (src.includes('ESWD') || src.map(Object.keys).flat().includes('ESWD')));
        }

        if (this.state['tor_collection'] !== null) {
            const geojson = await this.state['tor_collection'].getGeoJSON(this.state['interval'], undefined, false);
            const tors_no_eswd = geojson['features'].filter(f => !has_eswd(f['properties']['source']));
            if (tors_no_eswd.length > 0) {
                if (format == 'kml') {
                    downloadKML(tors_no_eswd, this.partitioner);
                }
                else if (format == 'csv') {
                    downloadCSV(tors_no_eswd);
                }
                else if (format == 'geojson') {
                    downloadGeoJSON(tors_no_eswd);
                }
            }
        }
    }

    render() {
        let map_intv: SelectionInterval | null = null; 

        const intfcs = this.state['env_src'] === null ? null : this.getEnvInterface(this.state['domain']);
        const intfc = intfcs === null || this.state['env_src'] === null ? null : intfcs[this.state['env_src']];

        if (this.state['sel_data_time'] !== null && intfc !== null
            && (this.state['temp_display'] == 'Hourly' || this.state['temp_display'] == 'All' && this.state['is_animating'])) {
            
            const dt_reanalysis = intfc.getAnalysisTimeStep();

            map_intv = new SelectionInterval(Interval.after(this.state['sel_data_time'], dt_reanalysis));
        }
        else if (this.state['temp_display'] == 'All') {
            map_intv = this.state['interval'];
        }

        let random_day_func = intfcs === null ? null : () => {
            const available_dates = Object.values(intfcs).map(intfc => intfc.available_dates).flat();
            return new Promise<DateTime>((resolve, reject) => {
                resolve(randomChoice(available_dates));
            });
        }
        
        if (random_day_func === null) {
            const dom_tc = this.domain_tor_collection;
            random_day_func = dom_tc !== null ? () => dom_tc.getRandomDate(DOMAINS[this.state['domain']]['cday_offset']) : null;
        }

        return (
            <div id="main">
                <MapWidget 
                    controls_visible={this.state['controls_visible']}
                    interval={map_intv}
                    tor_coll={this.state['tor_collection']}
                    partitioner={this.partitioner}

                    env_data_times={this.state['env_data_times']}
                    sel_data_time={this.state['sel_data_time']} 
                    env_handler={intfc}
                    env_type={this.state['env_type']}
                    reanalysis_domain={DOMAINS[this.state['domain']]['env_name']}
                    reanalysis_src={this.state['env_src']}

                    cday_offset={DOMAINS[this.state['domain']]['cday_offset']}
                    units={DOMAINS[this.state['domain']]['units']}
                    fit_bounds={DOMAINS[this.state['domain']]['fit_bounds']}
                    map_position={this.state['map']}

                    setscreenshotfunc={this.set_screenshot_func}

                    onmove={this.onmapmove.bind(this)}
                    onmapready={this.onmapready.bind(this)}
                    onloadingstart={this.onloadingstart}
                    onloadingend={this.onloadingend}
                    onintervalchange={this.onintervalchange}
                />
                <MapOverlayWidget
                    controls_visible={this.state['controls_visible']}
                    interval={this.state['interval']}
                    tor_coll={this.state['tor_collection']}
                    filters={this.state['filters']}
                    data_limits={DATA_LIMITS}
                    sel_data_time={this.state['sel_data_time']}
                    env_handler={intfc}
                    env_type={this.state['env_type']}
                    env_src={this.state['env_src']}
                    temp_display={this.state['temp_display']}
                    cday_offset={DOMAINS[this.state['domain']]['cday_offset']}
                    units={DOMAINS[this.state['domain']]['units']}
                    attribution={DOMAINS[this.state['domain']]['attribution']}

                    window_size={this.state['window_size']}

                    setscreenshotfunc={this.set_screenshot_func}
                />
                <ControlWidget 
                    visible={this.state['controls_visible']}
                    interval={this.state['interval']}
                    tor_coll={this.state['tor_collection']}
                    partitioner={this.partitioner}
                    filters={this.state['filters']}
                    timeline_margins={TIMELINE_MARGINS}
                    timeline_taxis={this.state['timeline_taxis']}
                    data_limits={DATA_LIMITS}
                    cday_offset={DOMAINS[this.state['domain']]['cday_offset']}
                    units={DOMAINS[this.state['domain']]['units']}
                    domain_has_states={DOMAINS[this.state['domain']]['has_states']}

                    env_data_times={this.state['env_data_times']}
                    sel_data_time={this.state['sel_data_time']}
                    env_handlers={intfcs}
                    env_type={this.state['env_type']}
                    env_src={this.state['env_src']}
                    is_animating={this.state['is_animating']}
                    temp_display={this.state['temp_display']}
                    env_intv_dur_cutoff={ENV_INTV_DUR_CUTOFF}
                    random_day_func={random_day_func}
                    display_eswd_download_warning={DOMAINS[this.state['domain']]['attribution'].includes('ESWD')}

                    domains={DOMAIN_TITLES}
                    sel_domain={this.state['domain']}

                    window_size={this.state['window_size']}

                    onshowhidecontrols={this.onshowhidecontrols}
                    onintervalchange={this.onintervalchange}
                    onfilterchange={this.onfilterchange}
                    ontboundschange={this.ontboundschange}
                    onenvlayerchange={this.onenvlayerchange}
                    onenvtimechange={this.onenvtimechange}
                    onanimtoggle={this.onanimtoggle}
                    onanimspeedchange={this.onanimspeedchange}
                    onscreenshot={this.onscreenshot}
                    oncreategif={this.oncreategif}
                    ondomainchange={this.ondomainchange}
                    ontempdisplaychange={this.ontempdisplaychange}
                    ondownloadfile={this.ondownloadfile}

                    setscreenshotfunc={this.set_screenshot_func}
                />
                <div id='loading' style={{'display': this.state['is_loading_tors'] || this.state['is_drawing_tors'] ? 'block': 'none'}}>
                    <span>Loading ...</span>
                    <div>&nbsp;</div>
                </div>
            </div>
        );
    }
}

export default TornadoArchiveApp;
